import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = () => {
  return [
    { title: "民事事件 - 契約・損害賠償・不動産紛争の専門弁護士 | 法務法人フィサン" },
    { name: "description", content: "韓国ソウルの日本語対応法律事務所。契約紛争、損害賠償請求、不動産トラブル、労働問題など幅広い民事事件に対応。仮差押・仮処分などの保全処分も迅速対応。" },
    { name: "keywords", content: "民事事件, 契約紛争, 損害賠償, 不動産訴訟, 労働問題, 仮差押, 仮処分, 民事執行, 韓国弁護士, 日本語対応, ソウル法律事務所" },
    { property: "og:title", content: "民事事件 - 契約・損害賠償・不動産紛争の専門弁護士 | 法務法人フィサン" },
    { property: "og:description", content: "契約紛争、損害賠償請求、不動産トラブル、労働問題など幅広い民事事件に対応。日本語で丁寧にサポートします。" },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://jp-hwisang.co.kr/ja/services/civil" },
    { property: "og:image", content: "https://jp-hwisang.co.kr/images/jp-hwisang-card.jpg" },
    { property: "og:locale", content: "ja_JP" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "robots", content: "index, follow" },
  ];
};

export default function CivilDetail() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <a href="/ja" className="flex items-center justify-center md:justify-start">
              <img src="/images/no-padding-logo.png" alt="法務法人フィサン" className="h-8 w-auto opacity-90" />
            </a>

            <div className="flex items-center justify-center md:justify-end gap-4 md:gap-6">
              <nav className="flex items-center gap-4 md:gap-6 text-sm">
                <a href="/ja/office" className="text-gray-700 hover:text-black whitespace-nowrap">事務所・所在地</a>
                <a href="/ja/services" className="text-black font-medium whitespace-nowrap">業務分野</a>
                <a href="/ja/blog" className="text-gray-700 hover:text-black whitespace-nowrap">法律ブログ</a>
              </nav>
              <a
                href="https://jp-hwisang.co.kr"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 border border-gray-300 hover:border-gray-900 text-gray-700 hover:text-black text-sm transition-colors whitespace-nowrap"
              >
                한국어
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <section className="py-8 px-4 border-b border-gray-200">
        <div className="max-w-4xl mx-auto">
          <a href="/ja/services" className="inline-flex items-center text-sm text-gray-600 hover:text-black transition-colors">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            業務分野一覧に戻る
          </a>
        </div>
      </section>

      {/* Title Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-serif mb-6 text-gray-900">民事事件</h1>
          <div className="w-16 h-px bg-gray-400 mb-8"></div>
          <p className="text-xl text-gray-700 leading-relaxed">
            民事事件 ― 幅広い分野の紛争を的確に解決します
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-4">
        <div className="max-w-4xl mx-auto space-y-12">
          <div>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              日常生活やビジネスの中で発生する民事紛争は、時に大きなストレスや経済的損失をもたらします。
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              輝相（フィサン）は、契約・損害賠償・不動産・労働・投資など、民事全般の法的問題について、依頼者の立場を守りながら最適な解決を導くことを目指しています。
            </p>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">契約・損害賠償に関する紛争</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                契約不履行や債務不履行、取引上のトラブルなど、民事紛争の多くは「契約」に関する誤解や履行問題から生じます。契約書の内容分析・リスク評価・損害賠償請求の代理などを通じ、依頼者の権利を確実に守ります。
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">不動産・建設・再開発に関する法的対応</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                不動産売買や賃貸借契約、建設工事、再開発・再建築事業などにおいては、多くの関係者と複雑な法的関係が絡みます。不動産・建設分野の豊富な経験をもとに、事前のリスク管理から訴訟対応まで、総合的なサポートを提供します。
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">労働・投資・取引関連のトラブル解決</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                労働契約、投資取引、企業間取引など、経済活動におけるトラブルも迅速な対応が求められます。
              </p>
              <p>
                輝相（フィサン）は、事実関係の整理・交渉・訴訟対応を通じて、依頼者にとって最も現実的で有利な解決策を導きます。
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">保全処分・民事執行（仮差押・仮処分）</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                紛争の過程で、相手方の財産や権利が移転・隠匿されるおそれがある場合、迅速な仮差押・仮処分などの保全措置が必要です。
              </p>
              <p>
                当事務所は、初期段階から適切な証拠収集と法的対応を行い、依頼者の権利を確実に保護します。
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">日本人依頼者へのサポート体制</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                韓国の法制度や裁判手続は、日本とは異なる点が多くあります。
              </p>
              <p>
                輝相（フィサン）の弁護士は、日韓両国の法制度と文化の違いを理解し、日本語での丁寧な説明と透明な手続きを通じて、日本のクライアントが安心して法的対応を進められるよう支援いたします。
              </p>
              <p>
                どのような問題でも、私たちは依頼者とともに最善の道を見つけ出します。輝相（フィサン）は、誠実さと結果で信頼に応えます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            民事事件でお困りの方は、<br />お気軽にご相談ください。
          </p>
          <div className="space-y-4">
            <a href="tel:010-4729-3573" className="block text-gray-900 hover:text-gray-600 transition-colors">
              <p className="text-lg font-medium">010-4729-3573</p>
            </a>
            <a href="mailto:mgkim@hwisang.co.kr" className="block text-gray-900 hover:text-gray-600 transition-colors">
              <p className="text-lg font-medium">mgkim@hwisang.co.kr</p>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">法務法人フィサン</h5>
              <p className="text-sm text-gray-600 leading-relaxed">
                韓国を代表する法律事務所として、<br />
                専門的で信頼できる法律サービスを提供します
              </p>
            </div>
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">業務分野</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>刑事事件</li>
                <li>金融・不動産</li>
                <li>経営権紛争</li>
                <li>家事事件</li>
              </ul>
            </div>
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">お問い合わせ</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>電話: 010-4729-3573</li>
                <li>Email: mgkim@hwisang.co.kr</li>
                <li>住所: ソウル特別市江南区永東大路611, 13階</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-8 pt-8 text-center">
            <p className="text-xs text-gray-500">&copy; 2024 法務法人フィサン. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
